import { ICON_PROPS, postToHost, shortDate } from './shared.js';
import { useEffect, useState } from 'react';
import {
  Accordion,
  ActionIcon,
  Alert,
  AppShell,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Checkbox,
  Chip,
  Code,
  Collapse,
  Container,
  Divider,
  Drawer,
  Grid,
  Group,
  List,
  Loader,
  Menu,
  Modal,
  MultiSelect,
  NativeSelect,
  NavLink,
  NumberInput,
  Paper,
  PasswordInput,
  Progress,
  RingProgress,
  ScrollArea,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
  Tooltip,
  useMantineColorScheme,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArchive,
  IconArrowBackUp,
  IconBell,
  IconBolt,
  IconBrain,
  IconBriefcase,
  IconCalendar,
  IconChartBar,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconClock,
  IconCommand,
  IconDeviceMobile,
  IconDownload,
  IconEdit,
  IconFileText,
  IconFlag,
  IconFolder,
  IconGavel,
  IconHistory,
  IconMail,
  IconMailOff,
  IconMessageQuestion,
  IconMoonStars,
  IconNews,
  IconPaperclip,
  IconPencil,
  IconPhone,
  IconPlus,
  IconRadar,
  IconReload,
  IconReport,
  IconSchool,
  IconSearch,
  IconSend,
  IconSettings,
  IconShare,
  IconShield,
  IconSparkles,
  IconStar,
  IconStarFilled,
  IconTarget,
  IconTrash,
  IconUser,
  IconUserPlus,
  IconUsersGroup,
  IconWand,
  IconX,
} from '@tabler/icons-react';
import { api, getStoredToken } from '../../api.js';
// Round 48 follow-up (2026-07-15): AddinPage.tsx monolith split — calendar + people panels,
// moved VERBATIM. Layering: shared ← shared-components ← panels ← AddinPage.

export function CalendarPanel(): React.JSX.Element {
  type Appt = {
    outlook_entry_id: string;
    subject: string | null;
    location: string | null;
    body_redacted: string | null;
    organizer_addr: string | null;
    organizer_name: string | null;
    attendees_json: string | null;
    start_at: string;
    end_at: string;
    duration_min: number | null;
    is_all_day: number;
    is_recurring: number;
    meeting_status: string | null;
    online_meeting_url: string | null;
  };
  const [data, setData] = useState<{ count: number; appointments: Appt[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [days, setDays] = useState(7);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    setErr(null);
    fetch(`/addin/calendar/upcoming?days=${days}`, {
      headers: { authorization: `Bearer ${getStoredToken() ?? ''}` },
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((j) => { if (!cancelled) setData(j as { count: number; appointments: Appt[] }); })
      .catch((e) => { if (!cancelled) setErr(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [days, refreshTick]);

  function syncCalendarNow(): void {
    postToHost({ type: 'sync_calendar' });
    setTimeout(() => setRefreshTick((t) => t + 1), 1500);
  }

  if (busy && !data) return <Center p="xl"><Loader /></Center>;
  if (err) return (
    <Alert color="red" variant="light">
      Couldn't load calendar: {err}
      <Button size="xs" ml="sm" variant="light" onClick={() => setRefreshTick((t) => t + 1)}>Retry</Button>
    </Alert>
  );
  if (!data || data.count === 0) {
    return (
      <Stack>
        <Group justify="space-between">
          <Title order={3}>Calendar</Title>
          <Group>
            <Button size="xs" variant="default" onClick={syncCalendarNow}>Sync now</Button>
          </Group>
        </Group>
        <Alert color="gray" variant="light">
          <Stack gap="xs">
            <Text>No appointments synced yet, or no events in the next {days} days.</Text>
            <Text size="xs" c="dimmed">
              The addin syncs your calendar automatically when it opens. Click <strong>Sync now</strong> to trigger it manually,
              or wait a few seconds and click <strong>Retry</strong>.
            </Text>
          </Stack>
        </Alert>
      </Stack>
    );
  }

  // Group by relative day label.
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dayMs = 24 * 60 * 60 * 1000;
  function bucketFor(dt: Date): string {
    const start = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()).getTime();
    const delta = Math.round((start - todayStart) / dayMs);
    if (delta === 0) return 'Today';
    if (delta === 1) return 'Tomorrow';
    if (delta < 0) return 'Earlier';
    if (delta < 7) return dt.toLocaleDateString(undefined, { weekday: 'long' });
    return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  const buckets = new Map<string, Appt[]>();
  for (const a of data.appointments) {
    const key = bucketFor(new Date(a.start_at));
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key)!.push(a);
  }

  function fmtTime(iso: string, allDay: number): string {
    if (allDay) return 'all day';
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  function fmtRange(a: Appt): string {
    if (a.is_all_day) return 'All day';
    const s = fmtTime(a.start_at, 0);
    const e = fmtTime(a.end_at, 0);
    return `${s} – ${e}`;
  }

  return (
    <Stack>
      <Group justify="space-between" wrap="wrap">
        <Stack gap={0}>
          <Title order={3}>Calendar</Title>
          <Text size="sm" c="dimmed">
            {data.count} appointment{data.count === 1 ? '' : 's'} in the next {days} days · synced from Outlook
          </Text>
        </Stack>
        <Group>
          <Select
            size="xs"
            value={String(days)}
            onChange={(v) => setDays(Number.parseInt(v ?? '7', 10))}
            data={['1', '3', '7', '14', '30'].map((d) => ({ value: d, label: `Next ${d} day${d === '1' ? '' : 's'}` }))}
            w={140}
          />
          <Button size="xs" variant="default" onClick={() => setRefreshTick((t) => t + 1)}>Refresh</Button>
          <Button size="xs" onClick={syncCalendarNow}>Sync now</Button>
        </Group>
      </Group>

      {[...buckets.entries()].map(([bucket, list]) => (
        <Stack key={bucket} gap="xs">
          <Group gap="xs">
            <Title order={5}>{bucket}</Title>
            <Badge variant="light" color="gray" size="sm">{list.length}</Badge>
          </Group>
          {list.map((a) => {
            const att: Array<{ name?: string; addr?: string; type?: string; response?: string }> =
              a.attendees_json ? (() => { try { return JSON.parse(a.attendees_json!); } catch { return []; } })() : [];
            const cancelled = a.meeting_status === 'olMeetingCanceled' || a.meeting_status === 'cancelled';
            const color = cancelled ? 'gray' : 'blue';
            return (
              <Card
                key={a.outlook_entry_id}
                withBorder
                padding="sm"
                radius="md"
                style={{
                  borderLeft: `4px solid var(--mantine-color-${color}-6)`,
                  opacity: cancelled ? 0.6 : 1,
                  textDecoration: cancelled ? 'line-through' : undefined,
                }}
              >
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                    <Group gap="xs" wrap="nowrap">
                      <Text fw={600} truncate>
                        {a.subject || '(no subject)'}
                      </Text>
                      {a.is_recurring ? <Badge size="xs" color="grape" variant="light">recurring</Badge> : null}
                      {cancelled && <Badge size="xs" color="red" variant="filled">cancelled</Badge>}
                    </Group>
                    <Group gap="md" wrap="wrap">
                      <Text size="sm" c="dimmed" fw={500}>
                        🕐 {fmtRange(a)}
                      </Text>
                      {a.location && (
                        <Text size="sm" c="dimmed">
                          📍 {a.location}
                        </Text>
                      )}
                      {a.duration_min != null && !a.is_all_day && (
                        <Text size="xs" c="dimmed">
                          {a.duration_min} min
                        </Text>
                      )}
                    </Group>
                    {att.length > 0 && (
                      <Group gap={6} mt={4}>
                        {att.slice(0, 5).map((p, i) => (
                          <Badge
                            key={i}
                            size="xs"
                            variant="light"
                            color={
                              p.response === 'olResponseAccepted' ? 'green' :
                              p.response === 'olResponseDeclined' ? 'red' :
                              p.response === 'olResponseTentative' ? 'yellow' : 'gray'
                            }
                          >
                            {p.name || p.addr || '?'}
                          </Badge>
                        ))}
                        {att.length > 5 && (
                          <Badge size="xs" variant="outline">+{att.length - 5}</Badge>
                        )}
                      </Group>
                    )}
                  </Stack>
                  {a.online_meeting_url && (
                    <Button
                      size="xs"
                      variant="filled"
                      color="blue"
                      component="a"
                      href={a.online_meeting_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Join
                    </Button>
                  )}
                </Group>
              </Card>
            );
          })}
        </Stack>
      ))}
    </Stack>
  );
}

export function PeoplePanel({
  onOpenSender,
}: {
  onOpenSender: (email: string) => void;
}): React.JSX.Element {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinPeopleTop>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('');
  const [peopleSort, setPeopleSort] = useState<'volume' | 'last_seen' | 'name' | 'vip'>(() => {
    try { return (localStorage.getItem('addin_people_sort') as 'volume' | 'last_seen' | 'name' | 'vip') ?? 'volume'; }
    catch { return 'volume'; }
  });
  useEffect(() => { try { localStorage.setItem('addin_people_sort', peopleSort); } catch { /* ignore */ } }, [peopleSort]);
  function compareItems(a: { volume: number; last_seen: string; from_addr: string; is_vip: boolean }, b: typeof a): number {
    if (peopleSort === 'volume') return b.volume - a.volume;
    if (peopleSort === 'last_seen') return b.last_seen.localeCompare(a.last_seen);
    if (peopleSort === 'name') return a.from_addr.localeCompare(b.from_addr);
    if (peopleSort === 'vip') {
      if (a.is_vip !== b.is_vip) return a.is_vip ? -1 : 1;
      return b.volume - a.volume;
    }
    return 0;
  }
  // Round 47: debounced server-side keyword search. The previous version
  // only filtered the local list by from_addr/domain — so typing "infobip"
  // returned nothing because Infobip emails come from no-reply@broadnet.me.
  // Now the server searches subjects + bodies + senders via FTS and returns
  // the senders behind matching emails, regardless of from_addr.
  // Round 28-AB: bump on Refresh click to force a re-fetch even when
  // the filter hasn't changed.
  const [refreshTick, setRefreshTick] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      setBusy(true);
      setLoadError(null);
      api
        .addinPeopleTop(filter.trim() || undefined)
        .then((r) => { if (!cancelled) setData(r); })
        .catch((err) => {
          // Was missing — pre-fix, any transient error left data=null and
          // the panel stuck on "Could not load people" with no recovery
          // path. Now: surface the actual error + a retry control.
          if (!cancelled) {
            setLoadError(err instanceof Error ? err.message : String(err));
          }
        })
        .finally(() => { if (!cancelled) setBusy(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [filter, refreshTick]);
  if (busy && !data) return <Center p="xl"><Loader /></Center>;
  if (!data) return (
    <Alert color={loadError ? 'red' : 'gray'} variant="light">
      {loadError ? `Couldn't load people: ${loadError}` : 'Could not load people.'}
      <Button size="xs" ml="sm" variant="light" onClick={() => setRefreshTick((t) => t + 1)}>
        Retry
      </Button>
    </Alert>
  );
  const visible = data.items;
  // Group by domain for clean reading
  const byDomain = new Map<string, typeof data.items>();
  for (const p of visible) {
    const d = p.domain ?? '(no domain)';
    const arr = byDomain.get(d) ?? [];
    arr.push(p);
    byDomain.set(d, arr);
  }
  // Sort people inside each domain (drives both Accordion-panel order and the
  // domain header order indirectly via cumulative volume / latest contact).
  for (const [, arr] of byDomain) arr.sort(compareItems);
  const sortedDomains = [...byDomain.entries()].sort((a, b) => {
    if (peopleSort === 'last_seen') {
      const la = a[1].reduce((m, x) => x.last_seen > m ? x.last_seen : m, '');
      const lb = b[1].reduce((m, x) => x.last_seen > m ? x.last_seen : m, '');
      return lb.localeCompare(la);
    }
    if (peopleSort === 'name') return a[0].localeCompare(b[0]);
    if (peopleSort === 'vip') {
      const va = a[1].some((x) => x.is_vip) ? 1 : 0;
      const vb = b[1].some((x) => x.is_vip) ? 1 : 0;
      if (va !== vb) return vb - va;
    }
    return b[1].reduce((s, x) => s + x.volume, 0) - a[1].reduce((s, x) => s + x.volume, 0);
  });
  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Stack gap={0}>
          <Title order={4}>People</Title>
          <Text size="xs" c="dimmed">
            Top correspondents (humans only) from the last 90 days. Click anyone for their profile, history, and VIP toggle.
            Position / phone / mobile auto-refresh every 4 hours via a background scan; click Refresh for an immediate re-fetch.
          </Text>
        </Stack>
        <Group gap="xs">
          <Button size="xs" variant="default" leftSection={<IconReload size={12} />}
            loading={busy}
            onClick={async () => {
              // Round 28-AB: Refresh now does a real signature rescan
              // (creates rows for top-volume senders that have none yet,
              // re-parses 50 oldest cached). Then re-fetches the panel.
              setBusy(true);
              try {
                const r = await api.addinPeopleRescanSignatures();
                if (r.changed > 0 || r.new_senders > 0) {
                  notifications.show({
                    color: 'green',
                    message: `Rescanned: ${r.changed} updated, ${r.new_senders} new senders parsed`,
                  });
                }
              } catch (e) {
                notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Rescan failed' });
              } finally {
                setRefreshTick((t) => t + 1);
              }
            }}>
            Refresh
          </Button>
          <Button size="xs" variant="subtle" leftSection={<IconFileText size={12} />}
            onClick={() => {
              const esc = (s: string | number | boolean | null | undefined) => {
                if (s == null) return '';
                const v = String(s);
                return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
              };
              const lines = ['from_addr,domain,title,phone,mobile,volume,first_seen,last_seen,is_vip'];
              for (const [, arr] of sortedDomains) {
                for (const p of arr) {
                  lines.push([p.from_addr, p.domain, p.title, p.phone, p.mobile, p.volume, p.first_seen, p.last_seen, p.is_vip].map(esc).join(','));
                }
              }
              const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `people-${new Date().toISOString().slice(0, 10)}.csv`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
            }}>
            CSV
          </Button>
          <Select
            size="xs"
            w={150}
            value={peopleSort}
            onChange={(v) => v && setPeopleSort(v as typeof peopleSort)}
            data={[
              { value: 'volume', label: 'Volume' },
              { value: 'last_seen', label: 'Last contact' },
              { value: 'name', label: 'Name' },
              { value: 'vip', label: 'VIP first' },
            ]}
          />
          <TextInput
            size="xs"
            placeholder="Search by name, company, email or topic…"
            value={filter}
            onChange={(e) => setFilter(e.currentTarget.value)}
            w={300}
            rightSection={busy ? <Loader size="xs" /> : null}
          />
        </Group>
      </Group>
      <Accordion multiple variant="separated">
        {sortedDomains.map(([domain, people]) => (
          <Accordion.Item key={domain} value={domain}>
            <Accordion.Control>
              <Group justify="space-between" pr="md">
                <Group gap="xs">
                  <IconFolder {...ICON_PROPS} />
                  <Text fw={600}>{domain}</Text>
                  <Badge size="sm" variant="light">{people.length} people</Badge>
                </Group>
                <Badge size="sm" color="blue">{people.reduce((s, x) => s + x.volume, 0)} emails</Badge>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap={2}>
                {people.map((p) => (
                  <Paper key={p.from_addr} p="xs" withBorder style={{ cursor: 'pointer' }}
                    onClick={() => onOpenSender(p.from_addr)}>
                    <Stack gap={2}>
                      <Group justify="space-between" wrap="nowrap">
                        <Group gap={4} style={{ flex: 1, minWidth: 0 }}>
                          {p.is_vip && <IconStarFilled size={14} color="var(--mantine-color-yellow-6)" />}
                          <Text size="sm" fw={500} lineClamp={1}>{p.from_addr}</Text>
                        </Group>
                        <Group gap={4}>
                          <Badge size="xs" variant="light">{p.volume} emails</Badge>
                          <Text size="xs" c="dimmed">last {shortDate(p.last_seen)}</Text>
                        </Group>
                      </Group>
                      {(p.title || p.mobile || p.phone) && (
                        <Group gap="md" wrap="wrap">
                          {p.title && (
                            <Group gap={4} wrap="nowrap">
                              <IconBriefcase size={12} color="var(--mantine-color-dimmed)" />
                              <Text size="xs" c="dimmed" lineClamp={1}>{p.title}</Text>
                            </Group>
                          )}
                          {p.mobile && (
                            <Group gap={4} wrap="nowrap" onClick={(e) => e.stopPropagation()}>
                              <IconDeviceMobile size={12} color="var(--mantine-color-dimmed)" />
                              <Text size="xs" c="dimmed" component="a"
                                href={`tel:${p.mobile}`} style={{ textDecoration: 'none' }}>
                                {p.mobile}
                              </Text>
                            </Group>
                          )}
                          {p.phone && (
                            <Group gap={4} wrap="nowrap" onClick={(e) => e.stopPropagation()}>
                              <IconPhone size={12} color="var(--mantine-color-dimmed)" />
                              <Text size="xs" c="dimmed" component="a"
                                href={`tel:${p.phone}`} style={{ textDecoration: 'none' }}>
                                {p.phone}
                              </Text>
                            </Group>
                          )}
                        </Group>
                      )}
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>
    </Stack>
  );
}

// =========================================================================
// Round 28-AC Phase B: in-addin self-update UI.
//
// Two surfaces:
//   - UpdateAvailableModal: dismissible "new version available" popup.
//   - UpdateBlockerOverlay: full-screen blocker — version is below the
//     force_below threshold; the addin can't be used until they update.
//
// Both share the same install flow: the user clicks "Update Now", the
// dashboard posts {type:'apply_update'} to the COM addin, and progress
// events stream back as the addin downloads + verifies + installs.
// =========================================================================

